import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, AlertCircle, CheckCircle2, RefreshCw, Send } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * iter103 — Admin email diagnostics card.
 *
 * Two buttons:
 *   1. "Run health check" → GET /admin/email/health
 *      Reads live Resend env vars + pings Resend Domains API. Shows masked API
 *      key prefix, sender email, verified-domain list, and any actionable
 *      warnings (missing key, default sender, unverified domain, etc.).
 *
 *   2. "Send test to my own inbox" → POST /admin/email/send-self-test
 *      Fires a single activation-template email to the logged-in admin.
 *      Returns the literal Resend error message on failure so admin can fix
 *      the env config without backend log access.
 *
 * Designed to be the FIRST thing admin checks when bulk-send fails on prod.
 */
export default function EmailDiagnosticsCard() {
  const [health, setHealth] = useState(null);
  const [healthBusy, setHealthBusy] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const runHealth = async () => {
    setHealthBusy(true);
    try {
      const { data } = await api.get(`/admin/email/health`);
      setHealth(data);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setHealthBusy(false);
    }
  };

  const sendSelfTest = async () => {
    setTestBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post(`/admin/email/send-self-test`, {});
      setTestResult(data);
      if (data?.status === "sent") {
        toast.success(`Test email sent to ${data.to} (Resend ID: ${data.resend_id?.slice(0, 8)}…). Check your inbox.`);
      } else {
        toast.error(`Test FAILED: ${data?.error?.slice(0, 200) || "Unknown error"}`, { duration: 15000 });
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setTestBusy(false);
    }
  };

  const w = health?.warnings || [];
  // iter103 — A "sending-only" Resend key returns 401 on /domains but still
  // sends emails fine. Treat as OK if no other warnings present.
  const allOk = health && w.length === 0 && health.resend_api_key_present &&
    (!health.domains_error || health.restricted_key);

  return (
    <div className="mb-10 p-5 border hairline bg-white" data-testid="email-diagnostics-card">
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="eyebrow mb-1 flex items-center gap-2">
            <Mail className="w-3 h-3" /> Email diagnostics
          </div>
          <div className="font-display text-xl mb-1">Resend health check</div>
          <p className="text-sm text-[#555] max-w-2xl">
            Run these before the activation blast. They confirm your Resend API key is valid, your sender
            domain is verified, and a real test email actually lands. The most common production failure is{" "}
            <code className="mono text-[12px] bg-[#fafafa] px-1">SENDER_EMAIL</code> falling back to the
            Resend default (which blocks external recipients).
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={runHealth}
          disabled={healthBusy}
          className="inline-flex items-center gap-1.5 border hairline px-3 py-2 text-[12px] text-[#111] bg-white hover:bg-[#fafafa] disabled:opacity-50"
          data-testid="email-health-btn"
        >
          {healthBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Run health check
        </button>
        <button
          type="button"
          onClick={sendSelfTest}
          disabled={testBusy}
          className="inline-flex items-center gap-1.5 bg-[#ff4500] text-white px-3 py-2 text-[12px] uppercase tracking-wider font-bold hover:bg-[#e53d00] disabled:opacity-50"
          data-testid="email-self-test-btn"
        >
          {testBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send test to my own inbox
        </button>
      </div>

      {/* Health-check result */}
      {health && (
        <div
          className={`border hairline p-4 ${allOk ? "bg-[#f5fdf7] border-[#0a7f3f]" : "bg-[#fff8f5]"}`}
          data-testid="email-health-result"
        >
          <div className="flex items-center gap-2 mb-3 font-display text-sm">
            {allOk ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-[#0a7f3f]" />
                <span>All systems go — emails should send fine.</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-4 h-4 text-[#b00020]" />
                <span>{w.length} issue(s) found — emails will fail until fixed.</span>
              </>
            )}
          </div>

          {/* Config rows */}
          <dl className="grid grid-cols-[140px_1fr] gap-y-1 text-[12px] mono mb-3">
            <dt className="text-[#888] uppercase">API key</dt>
            <dd className={health.resend_api_key_present ? "text-[#111]" : "text-[#b00020]"}>
              {health.resend_api_key_present ? health.resend_api_key_prefix : "MISSING"}
            </dd>
            <dt className="text-[#888] uppercase">Sender email</dt>
            <dd className={health.using_default_sender ? "text-[#b00020]" : "text-[#111]"}>
              {health.sender_email}
              {health.using_default_sender && " ⚠️ default"}
            </dd>
            <dt className="text-[#888] uppercase">Sender name</dt>
            <dd className="text-[#111]">{health.sender_name || "(unset)"}</dd>
            <dt className="text-[#888] uppercase">Verified domains</dt>
            <dd className="text-[#111]">
              {health.restricted_key ? (
                <span className="text-[#888]">Sending-only key (can't list domains — normal for prod)</span>
              ) : health.domains.length === 0 ? (
                <span className="text-[#888]">None found</span>
              ) : (
                health.domains.map((d, i) => (
                  <span
                    key={i}
                    className={`inline-block mr-2 ${d.status === "verified" ? "text-[#0a7f3f]" : "text-[#b00020]"}`}
                  >
                    {d.name} ({d.status})
                  </span>
                ))
              )}
            </dd>
          </dl>

          {health.domains_error && !health.restricted_key && (
            <div className="text-[11px] mono text-[#b00020] mb-2">
              Resend API error: {health.domains_error}
            </div>
          )}
          {health.restricted_key && (
            <div className="text-[11px] text-[#555] mb-2">
              ℹ️ {health.domains_error}
            </div>
          )}

          {w.length > 0 && (
            <ul className="space-y-2 text-[12px] text-[#b00020]">
              {w.map((msg, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span className="text-[#333] leading-relaxed">{msg}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Self-test result */}
      {testResult && (
        <div
          className={`mt-3 border hairline p-3 text-[12px] ${testResult.status === "sent" ? "bg-[#f5fdf7]" : "bg-[#fff0f0]"}`}
          data-testid="email-self-test-result"
        >
          {testResult.status === "sent" ? (
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#0a7f3f] mt-0.5 shrink-0" />
              <div>
                <div className="font-bold">Test email sent to {testResult.to}</div>
                <div className="mono text-[11px] text-[#555] mt-1">Resend ID: {testResult.resend_id}</div>
                <div className="text-[#555] mt-1">Check your inbox (and spam folder). If it never arrives, the issue is downstream of Resend.</div>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-[#b00020] mt-0.5 shrink-0" />
              <div>
                <div className="font-bold text-[#b00020]">Test failed — {testResult.to}</div>
                <div className="mono text-[11px] text-[#333] mt-1 whitespace-pre-wrap">{testResult.error || "(no error returned)"}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
